import { useState } from 'react';
import { ApiError } from '../lib/api';
import { useI18n, LANGUAGES, LANGUAGE_LABELS } from '../lib/i18n';
import { useAuthMethods, useRequestOtp, useVerifyOtp } from '../lib/queries';
import { Button, Field, Logo } from '../components/ui';

function LanguagePicker() {
  const { language, setLanguage } = useI18n();
  return (
    <div className="flex gap-1.5">
      {LANGUAGES.map((code) => (
        <button
          key={code}
          onClick={() => setLanguage(code)}
          aria-pressed={language === code}
          className={`inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg px-2.5 text-xs font-medium transition-colors ${
            language === code ? 'bg-raised text-ink' : 'text-ink-faint hover:text-ink-dim'
          }`}
        >
          {LANGUAGE_LABELS[code]}
        </button>
      ))}
    </div>
  );
}

export function LoginPage() {
  const { t, language } = useI18n();
  const { data: methods, isPending } = useAuthMethods();
  const request = useRequestOtp();
  const verify = useVerifyOtp();
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [stage, setStage] = useState<'phone' | 'code'>('phone');

  const otpAvailable = methods?.otp.available ?? false;
  const error = verify.error instanceof ApiError ? verify.error : null;

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-sm flex-col px-5 pb-[max(2rem,env(safe-area-inset-bottom))] pt-[max(2.5rem,env(safe-area-inset-top))]">
      <div className="flex items-start justify-between">
        <Logo size="lg" />
        <LanguagePicker />
      </div>

      <div className="mt-12 flex-1">
        <h1 className="font-display text-[2rem] font-bold leading-tight tracking-tight text-ink">
          {t('login.title')}
        </h1>

        {isPending && <p className="mt-3 text-sm text-ink-faint">{t('common.loading')}</p>}

        {!isPending && !otpAvailable && (
          <p className="mt-3 text-[0.95rem] leading-relaxed text-ink-dim">{t('login.noOtp')}</p>
        )}

        {!isPending && otpAvailable && stage === 'phone' && (
          <form
            className="mt-7 flex flex-col gap-5"
            onSubmit={(event) => {
              event.preventDefault();
              request.mutate({ phone, language }, { onSuccess: () => setStage('code') });
            }}
          >
            <p className="text-[0.95rem] leading-relaxed text-ink-dim">{t('login.subtitle')}</p>
            <Field
              label={t('login.phone')}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              autoFocus
              placeholder="555 00 12 34"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
            />
            <Button type="submit" loading={request.isPending} disabled={phone.trim().length < 5}>
              {t('login.send')}
            </Button>
          </form>
        )}

        {!isPending && otpAvailable && stage === 'code' && (
          <form
            className="mt-7 flex flex-col gap-5"
            onSubmit={(event) => {
              event.preventDefault();
              verify.mutate({ phone, code });
            }}
          >
            <p className="text-[0.9rem] leading-relaxed text-ink-faint">{t('login.sent')}</p>
            <Field
              label={t('login.code')}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={8}
              placeholder="••••••"
              className="tabular text-center text-2xl tracking-[0.4em]"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
            />
            {error && (
              <p role="alert" className="text-sm text-danger">{error.message}</p>
            )}
            <Button type="submit" loading={verify.isPending} disabled={code.length < 4}>
              {t('login.verify')}
            </Button>
            <Button variant="quiet" type="button" onClick={() => { setStage('phone'); setCode(''); }}>
              {t('login.back')}
            </Button>
          </form>
        )}
      </div>

      <p className="text-center text-[0.7rem] text-ink-faint/70">
        Auditoria Detailing · Tbilisi
      </p>
    </main>
  );
}
